import { useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import {
  CHAT_BUNDLE_URL,
  CHAT_ELEMENT_ID,
  CHAT_PLATFORM,
  CHAT_SCRIPT_ELEMENT_ID,
  DEFAULT_API_LANGUAGE,
} from '../commons/constants';
import { storeName as layoutModelStore } from '../redux/models/layout.model';
import { getProject } from '../redux/selectors/project.selector';
import { isInIframe } from '../utils';

declare global {
  interface Window {
    ChatComponent: {
      init: (props: {
        elementId: string;
        productCode: string;
        language: string;
        platform: string;
      }) => void;
    };
    openPopup: () => void;
  }
}

type UseChatType = {
  openPopup: () => void;
};

const useChat = (): UseChatType => {
  const locale = useSelector((state) => state[layoutModelStore].locale);
  const project = useSelector(getProject);
  const scriptLoaded = useSelector((state) => state[layoutModelStore].chatScriptLoaded);
  const layoutDispatch = useDispatch()[layoutModelStore];
  const inIframe = isInIframe();

  useEffect(() => {
    if (scriptLoaded || inIframe || document.getElementById(CHAT_SCRIPT_ELEMENT_ID)) {
      return;
    }

    // Create a new script element
    const script = document.createElement('script');
    script.id = CHAT_SCRIPT_ELEMENT_ID;
    script.src = CHAT_BUNDLE_URL;
    script.async = true;

    // Set up a callback for when the script is loaded
    script.onload = () => {
      layoutDispatch.setChatScriptLoaded(true);
    };

    // Append the script to the document
    document.body.appendChild(script);
  }, [scriptLoaded, inIframe]);

  useEffect(() => {
    const isNotInitChat =
      !project || !project?.hasAIQuestion || !scriptLoaded || !window.ChatComponent?.init;
    if (isNotInitChat) return;

    const language = locale?.apiLanguage || DEFAULT_API_LANGUAGE;
    const projectCode = project?.code || '';

    window.ChatComponent.init({
      elementId: CHAT_ELEMENT_ID,
      productCode: projectCode,
      language: language,
      platform: CHAT_PLATFORM,
    });
  }, [scriptLoaded, project, locale]);

  const openPopup = () => {
    if (window.openPopup) window.openPopup();
  };

  return useMemo(() => ({ openPopup }), [openPopup]);
};

export default useChat;
